import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Animated, Keyboard, Platform, StyleSheet, Text, TouchableOpacity, View } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { ThemedTextInput } from "./input/textinputs/themed-text-input"
import { ThemedDropdown } from "./input/misc/themed-dropdown"
import { MAX_CONTENT_WIDTH } from "../config"
import { Ionicons, MaterialIcons, FontAwesome } from "@expo/vector-icons"
// import { Company } from "@ecountabl/lib"
import { textStyles } from "../style"
import useScreenSize from "./screen-size"
import { ThemedButton } from "./input/buttons"
import { useAppDispatch, useAppSelector } from '../store'
import AutoShrinkText from "./AutoShrinkText"
import { Link, useNavigation, useRoute } from "@react-navigation/native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { usePlaidLink } from "../utility/context"
import Haptics from "./haptics"
import Analytics from "./analytics"
import Badge from "./badge"
import { completeOnboarding } from "../auth-slice"
import { onboardingSteps } from "../navigation/nav-stacks/OnboardingStack"
import { usePopover, showPopoverFromRef } from "./popover"

type ScreenHeaderProps = {
  goBackEnabled?: boolean
  functionality?: "search-company" | "search-transaction" | "settings" | "add-account" | "complete-onboarding" | "institution-actions"
  title?: string
}

type TransactionSort = "date new" | "date old" | "amount high" | "amount low"
type TransactionFilter = "any" | "values" | "not-values"
type TransactionTypeFilter = "all" | "income" | "expense"
type CompanySort = "relevance" | "name_asc" | "name_desc"
type ValuesMode = "unset" | "all" | "none" | "selected"
type SearchState = {
  term: string
  sort: TransactionSort
  filter: TransactionFilter
  txType: TransactionTypeFilter
  companySort: CompanySort
  valuesMode: ValuesMode
  selectedValues: string[]
}

type ScreenHeaderRouteParams = {
  term?: string
  searchTerm?: string
  sort?: string
  filter?: "any" | "values" | "not-values"
  txType?: "all" | "income" | "expense"
  values?: string | string[]
}
type ScreenHeaderRoute = { params?: ScreenHeaderRouteParams }
type NavWithSetParams = {
  setParams: (params: ScreenHeaderRouteParams) => void
  goBack: () => void
  navigate: (name: string, params?: Record<string, unknown>) => void
}

const TransactionSortOptions = [
  { label: "Newest to Oldest", value: "date new" },
  { label: "Oldest to Newest", value: "date old" },
  { label: "Amount High to Low", value: "amount high" },
  { label: "Amount Low to High", value: "amount low" },
]
const TransactionTypeOptions = [
  { label: "All", value: "all" },
  { label: "Income", value: "income" },
  { label: "Expense", value: "expense" },
]
const CompanySortOptions = [
  { label: "Relevance", value: "relevance" },
  { label: "Name A to Z", value: "name_asc" },
  { label: "Name Z to A", value: "name_desc" },
]
const DEFAULT_SEARCH_STATE: SearchState = {
  term: "",
  sort: "date new",
  filter: "any",
  txType: "all",
  companySort: "relevance",
  valuesMode: "unset",
  selectedValues: [],
}

const ScreenHeader: React.FC<ScreenHeaderProps> = ({
  goBackEnabled, functionality, title
}: ScreenHeaderProps) => {
  const { user } = useAppSelector(s => s.auth)
  const { institutions } = useAppSelector(s => s.accountsSlice)
  const { map: valuesMap } = useAppSelector(s => s.values)
  const institutionsWithErrors = useMemo(() => {
    return institutions.filter(institution => institution.error)
  }, [institutions])

  const insets = useSafeAreaInsets()
  const navigation = useNavigation() as unknown as NavWithSetParams
  const route = useRoute() as unknown as ScreenHeaderRoute

  const { startPlaidLinkSession, beginUnlinkPlaidAccount } = usePlaidLink()

  const theme = useContext(ThemeContext).theme
  const {
    bgMain,
    borderColor,
    white,
    danger,
    textMain,
    headerBackground,
    headerText,
  } = theme.colors

  // Use useRef to persist Animated.Value across renders
  const searchBarHeight = useRef(new Animated.Value(0)).current
  const searchRef = useRef(null)

  const dispatch = useAppDispatch()

  const routeName = useRoute().name

  const [expanded, setExpanded] = useState(false)
  const [currentSearch, setCurrentSearch] = useState<SearchState>(DEFAULT_SEARCH_STATE)
  const [leftWidth, setLeftWidth] = useState(0)
  const [rightWidth, setRightWidth] = useState(0)

  const {width, height} = useScreenSize()
  const { showFromAnchor, hide } = usePopover()
  const ellipsisRef = useRef<React.ElementRef<typeof TouchableOpacity>>(null)
  const mountedRef = useRef(true)
  const valueOptions = useMemo(() => {
    return Object.values(valuesMap || {})
      .map(v => {
        const valueRecord = v as unknown as Record<string, unknown>
        const color = (
          typeof valueRecord.color === "string"
            ? valueRecord.color
            : typeof valueRecord.hexColor === "string"
              ? valueRecord.hexColor
              : undefined
        )
        return {
          label: v.label || v.slug,
          slug: v.slug,
          color
        }
      })
      .filter(v => !!v.slug)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [valuesMap])
  const transactionValueOptions = useMemo(() => {
    const selectedSlugs = new Set<string>()
    const userMap = user?.valuedImpactAreaMap || {}

    Object.values(valuesMap || {}).forEach((value) => {
      const slug = typeof value?.slug === "string" ? value.slug : ""
      if (!slug) return
      const impactAreas = Array.isArray(value?.impactAreas) ? value.impactAreas : []
      const anySelectedImpact = impactAreas.some((ia) => !!userMap?.[ia?._id])
      if (anySelectedImpact) {
        selectedSlugs.add(slug)
      }
    })

    // If user has no selected values yet, keep full list to avoid empty UI.
    if (selectedSlugs.size === 0) {
      return valueOptions
    }

    return valueOptions.filter(v => selectedSlugs.has(v.slug))
  }, [user?.valuedImpactAreaMap, valueOptions, valuesMap])

  const normalizeParamArray = (param?: string | string[]) => {
    if (!param) return []
    return (Array.isArray(param) ? param : [param])
      .map(v => `${v}`.trim())
      .filter(Boolean)
  }

  const valuesQueryParamFromCurrentState = () => {
    if (currentSearch.valuesMode === "all") return "any"
    if (currentSearch.valuesMode === "none") return "none"
    if (currentSearch.valuesMode === "selected" && currentSearch.selectedValues.length > 0) {
      return currentSearch.selectedValues
    }
    return undefined
  }

  const toggleCurrentValue = (slug: string) => {
    setCurrentSearch(prev => {
      const isSelected = prev.selectedValues.includes(slug)
      const selectedValues = isSelected
        ? prev.selectedValues.filter(v => v !== slug)
        : [...prev.selectedValues, slug]

      return {
        ...prev,
        valuesMode: "selected",
        selectedValues
      }
    })
  }

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const toggleExpanded = () => {
    if (expanded) {
      Keyboard.dismiss()
    }
    setExpanded(!expanded)
  }

  const dismissKeyboardOnInteraction = () => {
    Keyboard.dismiss()
    return false
  }

  useEffect(() => {
    const expandSearchBar = () => {
      if (functionality === "search-transaction") {
        const rawValues = route?.params?.values
        const routeSort = route?.params?.sort
        const routeTxType = route?.params?.txType
        const normalizedRouteTxType = `${routeTxType || ""}`
        const normalizedSort: TransactionSort = (
          typeof routeSort === "string"
          && TransactionSortOptions.some(option => option.value === routeSort)
        )
          ? (routeSort as TransactionSort)
          : "date new"
        const normalizedTxType: TransactionTypeFilter = (
          typeof normalizedRouteTxType === "string"
          && TransactionTypeOptions.some(option => option.value === normalizedRouteTxType)
        )
          ? (normalizedRouteTxType as TransactionTypeFilter)
          : "all"
        const valuesFromParams = normalizeParamArray(rawValues)
          .filter(v => v !== "any" && v !== "none")
          .filter(v => transactionValueOptions.some(option => option.slug === v))
        setCurrentSearch({
          term: route?.params?.searchTerm || "",
          sort: normalizedSort,
          filter: route?.params?.filter || "any",
          txType: normalizedTxType,
          companySort: "relevance",
          valuesMode: valuesFromParams.length > 0
            ? "selected"
            : rawValues === "any"
              ? "all"
              : rawValues === "none"
                ? "none"
                : "unset",
          selectedValues: valuesFromParams,
        })
      } else if (functionality === "search-company") {
        setCurrentSearch(prev => ({
          ...prev,
          term: route?.params?.term || prev.term,
          companySort: (route?.params?.sort as CompanySort) || prev.companySort || "relevance",
          valuesMode: prev.valuesMode,
          selectedValues: prev.selectedValues,
        }))
      } else {
        setCurrentSearch(DEFAULT_SEARCH_STATE)
      }

      Animated.timing(searchBarHeight, {
        toValue: height,
        duration: 350,
        useNativeDriver: false,
      }).start(() => searchRef.current?.focus())
    }
  
    const closeSearchBar = () => {
      Animated.timing(searchBarHeight, {
        toValue: 0,
        duration: 350,
        useNativeDriver: false,
      }).start(() => {
        if (mountedRef.current && functionality === "search-company") {
          resetSearchToDefault()
        }
      })
    }

    if (expanded) {
      expandSearchBar()
    } else {
      closeSearchBar()
    }

  }, [expanded])

  const goBack = () => {
    navigation.goBack()
  }

  const openCompanySearchResults = (term?: string) => {
    const normalizedTerm = (term || currentSearch.term).trim()
    setExpanded(false)
    Analytics.track('company_search', {
      term: normalizedTerm,
    })
    navigation.navigate("CompanySearchResults", {
      term: normalizedTerm,
      sort: currentSearch.companySort === "relevance" ? undefined : currentSearch.companySort,
    })
  }

  const searchTransactions = async () => {
    const values = valuesQueryParamFromCurrentState()
    const legacyFilter: TransactionFilter | undefined = values === "none"
      ? "not-values"
      : Array.isArray(values) && values.length > 0
        ? "values"
        : values === "any"
          ? "any"
          : undefined

    Analytics.track('transaction_search', {
      term: currentSearch.term,
      sort: currentSearch.sort,
      filter: legacyFilter
    })

    // set route params to use current search params
    navigation.setParams({
      searchTerm: currentSearch.term,
      sort: currentSearch.sort as TransactionSort,
      filter: legacyFilter,
      txType: currentSearch.txType,
      values,
    })
  }

  const resetSearchToDefault = () => {
    const noDiff = JSON.stringify(currentSearch) === JSON.stringify(DEFAULT_SEARCH_STATE)
    if (noDiff) return

    // Defer to avoid update warnings from render-adjacent hooks on some platforms.
    setTimeout(() => {
      if (mountedRef.current) {
        setCurrentSearch(DEFAULT_SEARCH_STATE)
      }
    }, 0)
  }

  const renderCenterOfHeader = () => {
    return (
      <AutoShrinkText
        text={title ? title : user?.firstName ? `Hello, ${user.firstName}` : "Welcome"}
        textStyle={{...textStyles.h3, color: white}}
        containerStyle={{flexDirection: "row", justifyContent: "center"}}
        maxFontSize={StyleSheet.flatten(textStyles.h3).fontSize}
      />
    )
  }

  const renderSelectedValuesPicker = (
    options: Array<{label: string; slug: string; color?: string}> = valueOptions
  ) => {
    const valuesDisabled = currentSearch.valuesMode === "none" || currentSearch.valuesMode === "all"
    const allSelected = currentSearch.valuesMode === "all"
    const noneSelected = currentSearch.valuesMode === "none"

    return (
      <View style={{gap: 8}}>
        <Text style={[textStyles.label, {color: textMain}]}>Values Aligned</Text>
        <View style={{flexDirection: "row", flexWrap: "wrap", gap: 6}}>
          <TouchableOpacity
            onPress={() => {
              setCurrentSearch(prev => ({
                ...prev,
                valuesMode: allSelected ? "unset" : "all",
                selectedValues: []
              }))
            }}
            style={{
              borderWidth: 1,
              borderColor: allSelected ? textMain : borderColor,
              backgroundColor: allSelected ? `${textMain}40` : "transparent",
              borderRadius: 100,
              paddingHorizontal: 8,
              paddingVertical: 4
            }}
          >
            <Text style={[textStyles.subscript, {color: textMain}]}>Any</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setCurrentSearch(prev => ({
                ...prev,
                valuesMode: noneSelected ? "unset" : "none",
                selectedValues: []
              }))
            }}
            style={{
              borderWidth: 1,
              borderColor: noneSelected ? textMain : borderColor,
              backgroundColor: noneSelected ? `${textMain}40` : "transparent",
              borderRadius: 100,
              paddingHorizontal: 8,
              paddingVertical: 4
            }}
          >
            <Text style={[textStyles.subscript, {color: textMain}]}>None</Text>
          </TouchableOpacity>
        </View>
        <View style={{flexDirection: "row", flexWrap: "wrap", gap: 6, opacity: valuesDisabled ? 0.45 : 1}}>
          {options.map((value) => {
            const selected = currentSearch.selectedValues.includes(value.slug)
            return (
              <TouchableOpacity
                key={value.slug}
                disabled={valuesDisabled}
                onPress={() => {
                  if (valuesDisabled) return
                  toggleCurrentValue(value.slug)
                }}
                style={{
                  borderWidth: 1,
                  borderColor: selected ? (value.color || textMain) : borderColor,
                  backgroundColor: selected
                    ? `${value.color || textMain}40`
                    : "transparent",
                  borderRadius: 100,
                  paddingHorizontal: 8,
                  paddingVertical: 4
                }}
              >
                <Text style={[textStyles.subscript, {color: textMain}]}>
                  {value.label}
                </Text>
              </TouchableOpacity>
            )
          })}
        </View>
      </View>
    )
  }

  const renderSearchComponents = () => {
    if (functionality === "search-company") {
      return <>
        <Animated.View
          style={{
            width: "100%",
            height: searchBarHeight,
            position: "absolute",
            top: 50 + insets.top,
            overflow: "hidden",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <View
            style={{ backgroundColor: bgMain, width: "100%", justifyContent: "center", paddingVertical: 5, maxHeight: height - 150 }}
            onStartShouldSetResponderCapture={dismissKeyboardOnInteraction}
          >
            <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: "100%", alignSelf: "center", paddingHorizontal: MAX_CONTENT_WIDTH > width ? 16 : 0, gap: 10 }}>
              <ThemedTextInput
                ref={searchRef}
                value={currentSearch.term}
                onChange={(term) => setCurrentSearch(prev => ({ ...prev, term }))}
                placeholder="Search Companies"
                label="Company Name"
                textInputProps={{
                  returnKeyType: "search",
                  onSubmitEditing: (e) => openCompanySearchResults(e.nativeEvent.text),
                }}
              />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: 16,
                  rowGap: 16,
                  alignItems: "flex-start",
                }}
              >
                <ThemedDropdown
                  data={CompanySortOptions}
                  value={currentSearch.companySort}
                  onChange={(companySort) => {
                    setCurrentSearch(prev => ({ ...prev, companySort: companySort as CompanySort }))
                  }}
                  displayTextField="label"
                  valueField="value"
                  placeholder="Sort"
                  label="Sort By"
                  inputStyle={{
                    minWidth: 170,
                    height: 42,
                  }}
                />
              </View>
              {/* renderSelectedValuesPicker() */}
            </View>
            <View style={{width: "100%", flexDirection: "column", alignItems: "center", maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center", gap: 10, paddingHorizontal: 16, paddingVertical: 10}}>
              <ThemedButton
                text="Apply Search"
                onPress={() => openCompanySearchResults()}
                variant="primary"
              />
            </View>
          </View>
          <TouchableOpacity
            onPress={toggleExpanded}
            style={{flex: 1, backgroundColor: "rgba(0,0,0,0.5)", width: "100%"}}
            activeOpacity={1}
          />
        </Animated.View>
      </>
    } else if (functionality === "search-transaction") {
      return <>
        <Animated.View
          style={{
            width: "100%",
            height: searchBarHeight,
            position: "absolute",
            top: 50 + insets.top,
            overflow: "hidden",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <View
            style={{ backgroundColor: bgMain, width: "100%", justifyContent: "center", paddingVertical: 5, maxHeight: height - 150}}
            onStartShouldSetResponderCapture={dismissKeyboardOnInteraction}
          >
            <View
              style={{
                maxWidth: MAX_CONTENT_WIDTH,
                width: "100%",
                alignSelf: "center",
                paddingHorizontal: MAX_CONTENT_WIDTH > width ? 16 : 0,
              }}
            >
              <ThemedTextInput
                value={currentSearch.term}
                onChange={(term) => setCurrentSearch(prev => ({ ...prev, term }))}
                placeholder="Search Transactions"
                label="Transaction Name"
                textInputProps={{
                  returnKeyType: "done"
                }}
              />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  width: "100%",
                  paddingVertical: 10,
                }}
              >
                <ThemedDropdown
                  data={TransactionSortOptions}
                  value={currentSearch.sort}
                  onChange={(sort) => {
                    setCurrentSearch(prev => ({ ...prev, sort: sort as TransactionSort }))
                  }}
                  displayTextField="label"
                  valueField="value"
                  placeholder="Sort"
                  label="Sort By"
                  inputStyle={{
                    minWidth: 180,
                    height: 42,
                  }}
                />
                <ThemedDropdown
                  data={TransactionTypeOptions}
                  value={currentSearch.txType}
                  onChange={(txType) => {
                    setCurrentSearch(prev => ({ ...prev, txType: txType as TransactionTypeFilter }))
                  }}
                  displayTextField="label"
                  valueField="value"
                  placeholder="Filter"
                  label="Filter For"
                  inputStyle={{
                    width: 120,
                    height: 42,
                  }}
                />
              </View>
              {renderSelectedValuesPicker(transactionValueOptions)}
            </View>
            <View style={{width: "100%", flexDirection: "column", alignItems: "center", maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center", gap: 10, paddingHorizontal: 16, paddingVertical: 16}}>
              <ThemedButton
                text="Apply Search"
                onPress={() => {
                  searchTransactions()
                  setExpanded(false)
                }}
                variant="primary"
              />
            </View>
          </View>
          <TouchableOpacity
            onPress={toggleExpanded}
            style={{flex: 1, backgroundColor: "rgba(0,0,0,0.5)", height: "100%", width: "100%"}}
            activeOpacity={1}
          />
        </Animated.View>
      </>
    }
  }

  const renderFunctionalityComponent = () => {
    if (functionality?.startsWith("search-")) {
      return (
        <View style={[styles.iconContainer, styles.rightIconContainer]}>
          <MaterialIcons
            name={expanded ? "search-off" : "search"}
            size={24}
            color={headerText}
            onPress={() => {
              Haptics.selection()
              toggleExpanded()
            }}
          />
        </View>
      )
    } else if (functionality === "settings" && Platform.OS === "web") {
      return (
        <View style={[styles.iconContainer, styles.rightIconContainer]}>
          <Link
            screen={"Settings"}
            onPress={() => Haptics.selection()}
          >
            <View>
              <Ionicons name="settings" size={24} color={headerText} />
              {
                institutionsWithErrors.length > 0 && (
                  <View style={{ position: 'absolute', top: -10, right: -10 }}>
                    <Badge
                      style={{ backgroundColor: danger, height: 16, width: 16 }}
                      icon={
                        <Text style={[textStyles.subscript, { color: 'white' }]}>
                          {institutionsWithErrors.length}
                        </Text>
                      }
                    />
                  </View>
                )
              }
            </View>
            
          </Link>
        </View>
      )
    } else if (functionality === "add-account") {
      return (
        <View style={[styles.iconContainer, styles.rightIconContainer]}>
          <Ionicons
            name="add-circle"
            size={24}
            color={headerText}
            onPress={() => {
              Haptics.impactHeavy()
              startPlaidLinkSession({
                redirectPath: "settings/manage-accounts",
              })
            }}
          />
        </View>
      )
    } else if (functionality === "institution-actions") {
      // Use ellipsis to open a popover with Update + Delete actions
      return (
        <View style={[styles.iconContainer, styles.rightIconContainer]}>
          <TouchableOpacity
            ref={ellipsisRef}
            onPress={() => {
              Haptics.impactLight()
              const item_id = (route as any)?.params?.item_id
              const menu = (
                <View style={{ gap: 6 }}>
                  <TouchableOpacity
                    onPress={async () => {
                      Haptics.selection()
                      hide()

                      // sleep for 0.25 seconds to avoid UI freeze
                      await new Promise(resolve => setTimeout(resolve, 250))

                      if (item_id) {
                        startPlaidLinkSession({ item_id, redirectPath: "settings/manage-accounts" })
                      } else {
                        startPlaidLinkSession({ redirectPath: "settings/manage-accounts" })
                      }
                    }}
                  >
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8, padding: 6 }}>
                      <Ionicons name="refresh-outline" size={18} color={textMain} />
                      <Text style={[textStyles.body, { color: textMain }]}>
                        Update connection
                      </Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => {
                      Haptics.selection()
                      hide()
                      const item_id = (route as any)?.params?.item_id
                      if (item_id) {
                        const institution = institutions.find(i => i.item_id === item_id)
                        beginUnlinkPlaidAccount(item_id, institution?.name || "", "institution-details")
                      }
                    }}
                  >
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8, padding: 6 }}>
                      <Ionicons name="trash-outline" size={18} color={danger} />
                      <Text style={[textStyles.body, { color: textMain }]}>
                        Disconnect institution
                      </Text>
                    </View>
                  </TouchableOpacity>
                </View>
              )
              showPopoverFromRef(
                ellipsisRef,
                showFromAnchor,
                menu,
                undefined,
                { showArrow: true, offset: { x: 0, y: 0 } }
              )
            }}
            hitSlop={10}
          >
            <Ionicons name="ellipsis-vertical" size={20} color={headerText} />
          </TouchableOpacity>
        </View>
      )
    } else if (functionality === "complete-onboarding") {
      return (
        <View style={[styles.iconContainer, styles.rightIconContainer]}>
          <TouchableOpacity
            style={{flexDirection: "row", gap: 5, alignItems: "center", justifyContent: "center"}}
            onPress={() => {
              Haptics.selection()
              Analytics.track('onboarding_skipped', {
                step: routeName,
                step_index: onboardingSteps.findIndex(step => step.name === routeName),
              })
              dispatch(completeOnboarding())
            }}
          >
            <Text style={[textStyles.body, {color: headerText}]}>Skip</Text>
            <Ionicons name="arrow-forward" size={16} color={headerText} />
          </TouchableOpacity>
        </View>
      )
    }
  }

  const hasRightFunctionality = Boolean(functionality)
  const sideSlotWidth = Math.max(44, leftWidth, rightWidth)

  return (
    <View
      style={[
        styles.header,
        {
          backgroundColor: headerBackground,
          paddingTop: insets.top,
          height: insets.top + 50
        }
      ]}
    >
      {/* header content wrapper */}
      <View style={styles.headerContentWrapper}>
        {/* left slot: back button when enabled, otherwise placeholder for centering */}
        <View
          style={[styles.sideSlot, styles.leftSlot, { width: sideSlotWidth }]}
          onLayout={(event) => {
            const width = event.nativeEvent.layout.width
            if (width > leftWidth) {
              setLeftWidth(width)
            }
          }}
        >
          {goBackEnabled ? (
            <View style={[styles.iconContainer, styles.leftIconContainer]}>
              <FontAwesome
                name="arrow-left"
                size={24}
                color={headerText}
                onPress={() => {
                  Haptics.selection()
                  goBack()
                }}
              />
            </View>
          ) : (
            hasRightFunctionality ? <View style={styles.iconPlaceholder} /> : null
          )}
        </View>
        {/* center of header */}
        <View style={styles.centerSlot}>
          {renderCenterOfHeader()}
        </View>
        {/* functionality component */}
        <View
          style={[styles.sideSlot, styles.rightSlot, { width: sideSlotWidth }]}
          onLayout={(event) => {
            const width = event.nativeEvent.layout.width
            if (width > rightWidth) {
              setRightWidth(width)
            }
          }}
        >
          {renderFunctionalityComponent()}
        </View>
      </View>
      
      {renderSearchComponents()}
    </View>
  )
}

export default ScreenHeader

const styles = StyleSheet.create({
  header: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  headerContentWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 50,
    width: "100%",
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: "center",
    paddingHorizontal: 16,
    gap: 8
  },
  sideSlot: {
    height: "100%",
    justifyContent: "center",
  },
  leftSlot: {
    alignItems: "flex-start",
    paddingLeft: 0,
  },
  rightSlot: {
    alignItems: "flex-end",
    paddingRight: 0,
  },
  centerSlot: {
    flex: 1,
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  backArrow: {
    position: "absolute",
    left: 16,
    top: 16,
  },
  searchButton: {
    position: "absolute",
    right: 16,
    top: 16,
  },
  iconContainer: {
    justifyContent: "center",
    minWidth: 44,
    height: "100%",
    width: "100%",
    paddingHorizontal: 0,
  },
  leftIconContainer: {
    alignItems: "flex-start",
  },
  rightIconContainer: {
    alignItems: "flex-end",
  },
  iconPlaceholder: {
    minWidth: 44,
    height: 24,
  },
  companySearchResult:{
    width: "100%",
    justifyContent: "center",
    height: 50,
    paddingHorizontal: 16,
  },
  submitButtonWrapper: {
    paddingHorizontal: 16,
  }
})
